import { useMemo } from 'react';
import { fmtNum, fmtTRY } from '../../yeni-design/primitives';
import { parametreBildiriyor, useSaSorgu } from '../useSatisVerisi';
import { fisOzeti, sayi, type KanalSatiri, type Sayisal } from '../ozetHesaplari';
import { kanalSuz, subeSuz } from '../subeSuzgeci';
import { saatEtiketi } from '../ZamanBlok';
import { KanalKapsamNotu, KapsamNotu } from '../KapsamNotu';
import type { DetayGovdeProps } from './detayTipleri';
import { DagilimTablosu, DetayBolum, DetayMesaj, DetayYukleniyor, GunlukCubuklar, OzetKutulari } from './ortak';
import { kisaTutarIsaretsiz } from '../bicim';
import { kanalKirilimi, payHesapla, subeKirilimi } from './KanalDetay';

/** 990002 Saatlik Satış Dağılımı (SQL 137 sonrasi 'HH:00'). */
interface SaatlikSatiri {
  saat: Sayisal;
  fis_sayisi: Sayisal;
  ciro: Sayisal;
}

/** O gunun 990040 satirlari; sayfanin sube ve kanal suzgeci uygulanir (baslikta ikisi de yazar). */
export function gunDagilimi(rows: KanalSatiri[], gun: string, sube: string | null, kanal: string | null) {
  const g = kanalSuz(subeSuz(rows.filter((r) => String(r.tarih ?? '').slice(0, 10) === gun), sube), kanal);
  const oz = fisOzeti(g, null, null);
  return { satirVar: g.length > 0, ciro: oz.ciro, fis: oz.fis, kanallar: kanalKirilimi(g), subeler: subeKirilimi(g) };
}

/** M4 — gun detayi (spec §7.5). 990040 sayfa onbelleginden; 990002 o tek gun icin TEK istek. */
export function GunDetay({ kaynaklar, donem, sube, kanal, gun }: DetayGovdeProps & { gun: string }) {
  const q040 = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem, sube: null, kanal: null }, true, { onbellekten: true });
  const saatlikQ = useSaSorgu<SaatlikSatiri>(kaynaklar.saatlik002, { donem: { bas: gun, bit: gun }, sube, kanal });
  const veri = q040.data?.data;
  const d = useMemo(() => gunDagilimi(veri ?? [], gun, sube, kanal), [veri, gun, sube, kanal]);

  let dagilim;
  if (!kaynaklar.kanal040) dagilim = <DetayMesaj testid="gun-detay-kaynak-yok">Satış kanalı raporu bu firmada tanımlı değil.</DetayMesaj>;
  else if (q040.isLoading) dagilim = <DetayYukleniyor testid="gun-detay-yukleniyor" />;
  else if (q040.isError) dagilim = <DetayMesaj testid="gun-detay-hata">Gün verisi yüklenemedi.</DetayMesaj>;
  else if (!d.satirVar) dagilim = <DetayMesaj testid="gun-detay-bos">Bu günde satış yok.</DetayMesaj>;
  else
    dagilim = (
      <>
        <OzetKutulari
          kutular={[
            { testid: 'gun-detay-ozet-ciro', etiket: 'Ciro', deger: fmtTRY(d.ciro) },
            { testid: 'gun-detay-ozet-fis', etiket: 'Fiş', deger: fmtNum(d.fis) },
          ]}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DetayBolum baslik="Kanal dağılımı">
            <DagilimTablosu
              testid="gun-detay-kanallar"
              satirTestid="gun-detay-kanal"
              baslikSutunu="Kanal"
              satirlar={d.kanallar.map((k) => ({
                ad: k.kanal,
                ek: k.grup !== k.kanal ? k.grup : undefined,
                ciro: k.ciro,
                pay: payHesapla(k.ciro, d.ciro),
                sayac: k.fis,
              }))}
            />
          </DetayBolum>
          <DetayBolum baslik="Şube dağılımı">
            <DagilimTablosu
              testid="gun-detay-subeler"
              satirTestid="gun-detay-sube"
              baslikSutunu="Şube"
              satirlar={d.subeler.map((s) => ({ ad: s.lokasyon, ciro: s.ciro, pay: payHesapla(s.ciro, d.ciro), sayac: s.fis }))}
            />
          </DetayBolum>
        </div>
      </>
    );

  const saatlikVeri = saatlikQ.data?.data ?? [];
  let saatlik;
  if (!kaynaklar.saatlik002) saatlik = <DetayMesaj testid="gun-detay-saatlik-yok">Saatlik dağılım bu firmada tanımlı değil.</DetayMesaj>;
  else if (saatlikQ.isLoading) saatlik = <DetayYukleniyor testid="gun-detay-saatlik-yukleniyor" />;
  else if (saatlikQ.isError) saatlik = <DetayMesaj testid="gun-detay-saatlik-hata">Saatlik dağılım yüklenemedi.</DetayMesaj>;
  else if (saatlikVeri.length === 0) saatlik = <DetayMesaj testid="gun-detay-saatlik-bos">Bu günde saatlik satış yok.</DetayMesaj>;
  else
    saatlik = (
      <GunlukCubuklar
        testid="gun-detay-saatlik"
        noktalar={saatlikVeri.map((r) => {
          const etiket = saatEtiketi(r.saat);
          const ciro = sayi(r.ciro);
          return { anahtar: etiket, etiket, deger: ciro, baslik: `${etiket}: ${fmtTRY(ciro)}`, kisa: kisaTutarIsaretsiz(ciro) };
        })}
      />
    );

  return (
    <div data-testid="gun-detay" className="flex flex-col gap-4">
      {dagilim}
      <DetayBolum baslik="Saatlik ciro">
        {kaynaklar.saatlik002 && (sube !== null || kanal !== null) && (
          <p className="text-[12px] text-ink-500">
            <KapsamNotu sube={sube} destekli={parametreBildiriyor(kaynaklar.saatlik002, '@LOKASYON')} />
            <KanalKapsamNotu kanal={kanal} destekli={parametreBildiriyor(kaynaklar.saatlik002, '@KANAL')} />
          </p>
        )}
        {saatlik}
      </DetayBolum>
    </div>
  );
}
